import React from 'react';
import { Outlet, useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Flex, HStack, useDisclosure, VStack } from '@chakra-ui/react';
import { ReactComponent as MagixGroupLogo } from 'assets/icons/magixGroupLogo.svg';
import { useAccess, useProfile } from 'hooks';
import { NoMatch } from 'pages';
import { Banner, XMModal } from 'pages/Dashboard/components';
import { routes } from 'statics';

import { Header, MenuList } from 'components';

const DashboardLayout: React.FC = () => {
  const { data: profile, isLoading } = useProfile();
  const access = useAccess();

  const username =
    profile?.profile.first_name && profile?.profile.last_name
      ? profile?.profile.first_name + ' ' + profile?.profile.last_name
      : '';

  const ldLevel = (profile?.role.title || 'LD') === 'LD';
  const brokerLevel = (profile?.role.title || 'BROKER') === 'BROKER';

  const menuList = [
    {
      title: 'dashboard',
      link: '/dashboard',
      isActive: true,
    },
    {
      title: 'exchange',
      link: '/exchange',
      isActive: access.swapService,
    },
    {
      title: 'transfer',
      link: '/transfer',
      isActive: access.transferService && !ldLevel && !brokerLevel,
    },
    { title: 'wallets', link: '/wallets', isActive: access.walletService },
    {
      title: 'requests',
      link: '/requests',
      isActive: ldLevel && access.requestLDService,
    },
    { title: 'settings', link: '/settings', isActive: true },
  ];

  const { open, onClose, onOpen } = useDisclosure();

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  React.useEffect(() => {
    if (
      searchParams.get('system_identifier') === 'XM' &&
      access.verifyInitiateExternalServicePermission &&
      access.rejectInitiateExternalServicePermission
    ) {
      onOpen();
      setSearchParams({});
    }
    if (searchParams.get('return_url')) {
      navigate(searchParams.get('return_url') || '');
    }
  }, []);

  if (isLoading) {
    return <NoMatch />;
  }

  return (
    <Box bg="secondary" width="full" height="100%" pb="6" minH="100vh">
      <VStack display={{ base: 'flex', lg: 'none' }}>
        <Header
          username={username}
          level={profile?.role.title}
          isLoading={isLoading}
        />
        <Outlet />
      </VStack>
      <HStack
        height="full"
        gap="0"
        display={{ base: 'none', lg: 'flex' }}
        align="flex-start"
        position="relative"
      >
        <VStack
          position="fixed"
          left={0}
          top={0}
          align="stretch"
          w="288px"
          minW="288px"
          h="100vh"
          maxH="100vh"
          pt={8}
          pb={6}
          px={4}
          borderRight="1px solid"
          borderColor="body2"
          bg="sidebarbg"
          overflow="hidden"
          gap={0}
        >
          <Box
            flexShrink={0}
            mb={6}
            cursor="pointer"
            onClick={() => navigate(routes.dashboard.url)}
            alignSelf="flex-start"
            css={{
              '& svg': {
                display: 'block',
                maxWidth: '100px',
                width: '100%',
                height: 'auto',
              },
            }}
          >
            <MagixGroupLogo />
          </Box>
          <Flex direction="column" flex="1" minH={0} w="100%" overflowY="auto">
            <MenuList list={menuList.filter((item) => item.isActive)} />
          </Flex>
        </VStack>
        <Box flex="3" px="72px" h="full" pt="7" ml="288px">
          <Header
            username={username}
            level={profile?.role.title}
            isLoading={isLoading}
          />
          <br />
          <Banner />
          <Outlet />
        </Box>
      </HStack>
      <XMModal
        onClose={() => {
          onClose();
        }}
        isOpen={open}
      />
    </Box>
  );
};
export default DashboardLayout;
